import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import loadProductSelectionParts, {
    ProductSelectionPartsParameter,
} from "@tCooneyBrothers/Handlers/LoadProductSelectionParts";
import loadCarouselProducts from "@tCooneyBrothers/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import CustomApplicationState from "@tCooneyBrothers/Store/CustomApplicationState";

import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import Hidden from "@insite/mobius/Hidden";

import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SparePartComponent from "@tCooneyBrothers/Components/SparePartComponent";
import SparePartComponentMobile from "@tCooneyBrothers/Components/SparePartComponentMobile";

import SparePartImage from "@tCooneyBrothers/Components/SparePartImageComponent";
import sortBy from "lodash/sortBy";

import { getProductSelectionPartsCollectionV2 } from "@tCooneyBrothers/Store/Data/ProductSelectionParts/ProductSelectionPartsSelector";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    carouselType = "carouselType",
    displayProductsFrom = "displayProductsFrom",
    selectedCategoryIds = "selectedCategoryIds",
    relatedProductType = "relatedProductType",
    numberOfProductsToDisplay = "numberOfProductsToDisplay",
    seedWithManuallyAssigned = "seedWithManuallyAssigned",
    showImage = "showImage",
    showTitle = "showTitle",
    parentProduct = "parentProduct",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.carouselType]: string;
        [fields.displayProductsFrom]: string;
        [fields.selectedCategoryIds]: string[];
        [fields.relatedProductType]: string;
        [fields.numberOfProductsToDisplay]: number;
        [fields.seedWithManuallyAssigned]: string;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.parentProduct]: string;
    };
}

const mapStateToProps = (state: CustomApplicationState, ownProps: OwnProps) => ({
    products: getProductsForProductInfoList(state, ownProps.id),
    pageType: getCurrentPage(state).type,
    productSelectionData: getProductSelectionPartsCollectionV2(state),
});

const mapDispatchToProps = {
    loadCarouselProducts,
    loadProductSelectionParts,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface SparePartProductWidgetStyles {
    titleText?: TypographyPresentationProps;
    container?: InjectableCss;
    headerText?: TypographyProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableCells?: DataTableCellBaseProps;
    invoiceNumberHeader?: DataTableHeaderProps;
    invoiceDateHeader?: DataTableHeaderProps;
    termsHeader?: DataTableHeaderProps;
    dueDateHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    sparePartMobileStyles?: InjectableCss;
}

export const sparePartProductWidgetStyles: SparePartProductWidgetStyles = {
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    container: {
        css: css`
            overflow: auto;
        `,
    },
    shipToHeader: {
        css: css`
            width: 300px;
        `,
    },
    sparePartMobileStyles: {
        css: css`
            > [class^="GridWrapper"] {
                border-bottom: 1px solid !important;
                &:last-child {
                    border-bottom: none !important;
                }
            }
            [class^="GridItemStyle"] {
                padding: 5px 0;
            }
            [class^="GridOffset"] {
                margin: 0;
                width: 100% !important;
            }
            padding: 15px;
            h4 {
                font-weight: bold;
            }
            &:nth-child(odd) {
                background: #f3f3f3;
            }
        `,
    },
};

const styles = sparePartProductWidgetStyles;

const SparePartProductWidget: React.FC<Props> = ({
    id,
    theme,
    fields,
    products,
    loadCarouselProducts,
    productSelectionData,
    loadProductSelectionParts,
    pageType,
    shellContext,
}) => {
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";
    const brand = React.useContext(BrandStateContext).value;
    const category = React.useContext(CategoryContext);
    const productContext = React.useContext(ProductContext);
    const param: ProductSelectionPartsParameter = {
        productId: productContext?.product?.id,
    };
    React.useEffect(() => {
        loadProductSelectionParts(param);
    }, [productContext?.product?.id]);

    React.useEffect(() => {
        loadCarouselProducts({
            carouselId: id,
            carouselType: "relatedProducts",
            relatedProductType: "Product Selection Parts",
            seedWithManuallyAssigned: fields.seedWithManuallyAssigned,
            displayProductsFrom: fields.displayProductsFrom,
            selectedCategoryIds: fields.selectedCategoryIds,
            numberOfProductsToDisplay: fields.numberOfProductsToDisplay,
            isProductDetailsPage,
            productId: productContext?.product?.id,
            isProductListPage,
            category,
            isBrandDetailsPage,
            brand,
        });
    }, [
        fields.carouselType,
        fields.relatedProductType,
        fields.seedWithManuallyAssigned,
        fields.displayProductsFrom,
        fields.selectedCategoryIds,
        fields.numberOfProductsToDisplay,
        productContext?.product?.id,
        category?.id,
        brand?.id,
    ]);

    productSelectionData = sortBy(productSelectionData, o => {
        if (o.partNumber !== "undefined") {
            // eslint-disable-next-line radix
            return parseInt(o.partNumber);
        }
        return 0;
    });

    if (!products || products.length === 0) {
        return null;
    }

    return (
        <>
            <StyledWrapper>
                <SparePartImage parentProduct={fields.parentProduct} products={productSelectionData}></SparePartImage>
            </StyledWrapper>
            <Hidden below="lg">
                <StyledWrapper {...styles.container}>
                    <DataTable {...styles.dataTable}>
                        <DataTableHead {...styles.dataTableHead}>
                            <DataTableHeader tight {...styles.invoiceNumberHeader}>
                                {translate("Parts")}
                            </DataTableHeader>
                            <DataTableHeader tight {...styles.invoiceDateHeader}>
                                {translate("Image")}
                            </DataTableHeader>
                            <DataTableHeader tight {...styles.dueDateHeader}>
                                {translate("MFG Part Number")}
                            </DataTableHeader>
                            <DataTableHeader tight {...styles.shipToHeader}>
                                {translate("Description")}
                            </DataTableHeader>
                            <DataTableHeader tight {...styles.statusHeader}>
                                {translate("View Specs & Shop")}
                            </DataTableHeader>
                        </DataTableHead>
                        <DataTableBody {...styles.dataTableBody}>
                            {productSelectionData.map(part => {
                                const product =
                                    products.find(p => p.productNumber === part.productPartNumber) || products[0];
                                return (
                                    <>
                                        <SparePartComponent
                                            key={product?.id}
                                            carouselId={id}
                                            product={product}
                                            productSelectionPartsModel={part}
                                        />
                                    </>
                                );
                            })}
                        </DataTableBody>
                    </DataTable>
                </StyledWrapper>
            </Hidden>
            <Hidden above="md">
                <StyledWrapper {...styles.sparePartMobileStyles}>
                    {productSelectionData.map(part => {
                        const product = products.find(p => p.productNumber === part.productPartNumber) || products[0];
                        return (
                            <>
                                <GridContainer>
                                    <SparePartComponentMobile
                                        key={product?.id}
                                        carouselId={id}
                                        product={product}
                                        productSelectionPartsModel={part}
                                    />
                                </GridContainer>
                            </>
                        );
                    })}
                </StyledWrapper>
            </Hidden>
        </>
    );
};

const carouselTypeOptions = [{ value: "relatedProducts", displayName: "Related Products" }];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(SparePartProductWidget))),
    definition: {
        group: "Common",
        icon: "Carousel",
        fieldDefinitions: [
            {
                name: fields.displayProductsFrom,
                displayName: "Display Products From",
                editorTemplate: "DropDownField",
                options: [
                    { value: "allCategories", displayName: "All Categories" },
                    { value: "selectedCategories", displayName: "Selected Categories" },
                    { value: "customerSegments", displayName: "Customer Segments" },
                ],
                defaultValue: "allCategories",
                fieldType: "General",
                isVisible: item => item.fields[fields.carouselType] === "topSellers",
                sortOrder: 2,
            },
            {
                name: fields.selectedCategoryIds,
                displayName: "Selected Categories",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "General",
                isVisible: item =>
                    item.fields[fields.carouselType] === "topSellers" &&
                    item.fields[fields.displayProductsFrom] === "selectedCategories",
                sortOrder: 3,
            },
            {
                name: fields.numberOfProductsToDisplay,
                displayName: "Number Of Products To Display",
                editorTemplate: "IntegerField",
                min: 1,
                max: 20,
                defaultValue: 10,
                fieldType: "General",
                isVisible: item => item.fields[fields.carouselType] === "customersAlsoPurchased",
                sortOrder: 5,
            },
            {
                name: fields.seedWithManuallyAssigned,
                displayName: "Seed With Manually Assigned",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "General",
                isVisible: item => item.fields[fields.carouselType] === "customersAlsoPurchased",
                sortOrder: 6,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.parentProduct,
                displayName: "Parent Product Manufacturer Item Number",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
